import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../lib/classnames.js';
import { currentVideoTier } from '../lib/videoTier.js';

/**
 * HouseScrub — /new's backdrop: a walk through the House of Gilding.
 *
 * One continuous video timeline (six chambers concatenated, each ending in a
 * baked gold-bloom transit) scrubbed by scroll, ForestScrub-style: SCROLL IS
 * THE CLOCK. A piecewise CHAPTER MAP gives each chamber's HOLD most of its
 * scroll distance — the room moves slowly and stays alive under the form beat
 * that rides it — and compresses each TRANSIT into a short gap, so crossing
 * between beats feels like travelling through the bloom, not cutting.
 *
 * Orientation-aware: picks the portrait (9:16) or landscape (16:9) asset at
 * mount and on debounced resize, so phones scrub a native vertical master
 * instead of a cropped slice of a widescreen one.
 *
 * iOS hardening (each of these bit us once — none is optional):
 *  - ACTIVATION: Safari won't paint currentTime seeks until the element has
 *    genuinely played. On canplay we play ~350ms muted, pause, then hand the
 *    clock to scroll. If autoplay is refused (Low Power Mode), the first
 *    touch retries inside the gesture grant.
 *  - POSTER-FIRST: the chamber master still renders beneath the video from
 *    the first frame, so the page is never black while (or if ever) the
 *    video loads. Motion is an enhancement layer, not a dependency.
 *  - fastSeek where available — Safari's cheap path for all-keyframes media.
 *
 * The chapter map is exported (HOUSE_BEATS / HOUSE_BEAT_CENTRE) so the beat
 * overlays, BeatRail, GiltThread, and CommissionPlate all share one truth.
 */

/** Per-chamber hold fraction of its clip (rest is the transit). Must match
 *  the production encode (six equal-length clips, blooms in the tails). */
const HOLDS = [0.55, 0.6, 0.6, 0.6, 0.55, 1.0] as const;

/** Scroll-distance units: holds get 76, transits 24; the altar (terminus)
 *  holds 60 with no exit. Total 560 → mirrored by heightVh below. */
const UNITS: ReadonlyArray<{ u: number; type: 'hold' | 'transit'; chamber: number }> = (() => {
  const out: Array<{ u: number; type: 'hold' | 'transit'; chamber: number }> = [];
  for (let i = 0; i < 6; i += 1) {
    out.push({ u: i === 5 ? 60 : 76, type: 'hold', chamber: i });
    if (i < 5) out.push({ u: 24, type: 'transit', chamber: i });
  }
  return out;
})();
const TOTAL_UNITS = UNITS.reduce((s, x) => s + x.u, 0); // 560

/** Hold windows in scroll progress, derived from UNITS — the single source
 *  of truth for where each form beat lives over the timeline. */
export const HOUSE_HOLDS: ReadonlyArray<{ p0: number; p1: number }> = (() => {
  const out: Array<{ p0: number; p1: number }> = [];
  let acc = 0;
  for (const seg of UNITS) {
    const p0 = acc / TOTAL_UNITS;
    acc += seg.u;
    if (seg.type === 'hold') out.push({ p0, p1: acc / TOTAL_UNITS });
  }
  return out;
})();

/** driveBeat envelopes (a=rise start, b=fully in, c=fade start, d=gone),
 *  fitted inside each chamber's hold so beats surface over settled rooms
 *  and clear out before the transit rips. */
export const HOUSE_BEATS = {
  character: { a: 0.183, b: 0.215, c: 0.295, d: 0.345 },
  sketch: { a: 0.362, b: 0.394, c: 0.473, d: 0.523 },
  look: { a: 0.541, b: 0.573, c: 0.652, d: 0.7 },
  shape: { a: 0.719, b: 0.751, c: 0.83, d: 0.878 },
  strike: { a: 0.898, b: 0.94, c: 1.0, d: 1.0 },
} as const;

/** Jump targets (hold centres) for the BeatRail / CommissionPlate. */
export const HOUSE_BEAT_CENTRE = {
  threshold: 0.0,
  character: 0.25,
  sketch: 0.425,
  look: 0.6,
  shape: 0.78,
  strike: 0.96,
} as const;

/** GiltThread node positions along its rule (same centres). */
export const HOUSE_THREAD_POSITIONS = [0.03, 0.25, 0.425, 0.6, 0.78, 0.96] as const;

/** A single video source + how to scrub it. The full master is all-intra
 *  (every frame a keyframe) so fastSeek lands frame-exact; the mobile tier is
 *  a light 720p GOP-8 re-encode where fastSeek would snap to keyframe
 *  boundaries (~0.27s, visibly stepped), so it must be seeked with an exact
 *  currentTime (decodes ≤7 frames — trivial on any modern decoder). */
type HouseSource = { video: string; seek: 'fast' | 'exact' };
type HouseOrient = { full: HouseSource; mobile: HouseSource; poster: string };

/** Asset set per orientation, each with a full and a mobile-data tier. The
 *  full master is ~94MB all-intra 1080p; the mobile tier is a ~16MB 720p
 *  re-encode that is retina-sharp on a phone and starts fast on cellular.
 *  Dev serves from public/ so the tunnel works today; prod points at R2. */
export const HOUSE_MEDIA: Record<'portrait' | 'landscape', HouseOrient> = import.meta.env.DEV
  ? {
      portrait: {
        full: { video: '/house/house-scrub-portrait.mp4', seek: 'fast' },
        mobile: { video: '/house/house-scrub-portrait-720.mp4', seek: 'exact' },
        poster: 'https://api.gilding.app/images/gen/asset/house-doors-portrait-mrpzrw5v/openai-gen-0.png',
      },
      landscape: {
        full: { video: '/house/house-scrub-landscape.mp4', seek: 'fast' },
        mobile: { video: '/house/house-scrub-landscape-720.mp4', seek: 'exact' },
        poster: 'https://api.gilding.app/images/gen/asset/house-doors-mrpdu2z6/openai-gen-0.png',
      },
    }
  : {
      portrait: {
        full: { video: 'https://api.gilding.app/images/scenes/house/house-portrait.mp4', seek: 'fast' },
        mobile: { video: 'https://api.gilding.app/images/scenes/house/house-portrait-720.mp4', seek: 'exact' },
        poster: 'https://api.gilding.app/images/gen/asset/house-doors-portrait-mrpzrw5v/openai-gen-0.png',
      },
      landscape: {
        full: { video: 'https://api.gilding.app/images/scenes/house/house-landscape.mp4', seek: 'fast' },
        mobile: { video: 'https://api.gilding.app/images/scenes/house/house-landscape-720.mp4', seek: 'exact' },
        poster: 'https://api.gilding.app/images/gen/asset/house-doors-mrpdu2z6/openai-gen-0.png',
      },
    };

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function HouseScrub({
  heightVh = 560,
  className,
  children,
  onProgress,
  loading = false,
}: {
  heightVh?: number;
  className?: string;
  children?: ReactNode;
  onProgress?: (p: number) => void;
  /** When true, the video stops scroll-scrubbing and PLAYS the altar chamber
   *  segment on loop — a living loading scene (leaf trembling, embers
   *  breathing) instead of a frozen frame with an overlay graphic. */
  loading?: boolean;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const posterRef = useRef<HTMLImageElement>(null);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    const poster = posterRef.current;
    if (!section || !video || !poster) return undefined;

    // ---- orientation-aware source pick (no hot-swap mid-scroll; a debounced
    // resize that CHANGES orientation reloads the matching master) ----------
    let currentKey: string | null = null;
    let seekMode: 'fast' | 'exact' = 'fast';
    const pickSource = (): void => {
      const orient = window.matchMedia('(orientation: portrait)').matches
        ? 'portrait'
        : 'landscape';
      const tier = currentVideoTier();
      const key = `${orient}:${tier}`;
      if (key === currentKey) return;
      currentKey = key;
      const media = HOUSE_MEDIA[orient];
      const src = media[tier];
      seekMode = src.seek;
      activated = false;
      video.classList.remove('house-video-ready');
      video.src = src.video;
      poster.src = media.poster;
      video.load();
    };
    let resizeT = 0;
    const onResize = (): void => {
      window.clearTimeout(resizeT);
      resizeT = window.setTimeout(pickSource, 250);
    };

    // ---- chapter map ------------------------------------------------------
    interface Seg { p0: number; p1: number; t0: number; t1: number }
    let segs: Seg[] | null = null;
    const buildSegs = (): void => {
      if (!video.duration || Number.isNaN(video.duration)) return;
      const clip = video.duration / 6;
      let accU = 0;
      segs = UNITS.map((seg) => {
        const p0 = accU / TOTAL_UNITS;
        accU += seg.u;
        const p1 = accU / TOTAL_UNITS;
        const base = seg.chamber * clip;
        const holdT = HOLDS[seg.chamber]! * clip;
        return seg.type === 'hold'
          ? { p0, p1, t0: base, t1: base + holdT }
          : { p0, p1, t0: base + holdT, t1: base + clip - 0.05 };
      });
    };
    const timeFor = (p: number): number => {
      if (!segs) return 0;
      for (const s of segs) {
        if (p <= s.p1 || s === segs[segs.length - 1]) {
          const f = clamp01((p - s.p0) / (s.p1 - s.p0));
          return s.t0 + f * (s.t1 - s.t0);
        }
      }
      return 0;
    };

    // ---- iOS activation ---------------------------------------------------
    let activated = false;
    const activate = (): void => {
      if (activated) return;
      // play() resolves on modern browsers, is undefined on ancient ones;
      // Promise.resolve() normalizes both without a promise-in-conditional.
      void Promise.resolve(video.play())
        .then(() => {
          window.setTimeout(() => {
            video.pause();
            activated = true;
            lastSet = -1;
            video.classList.add('house-video-ready');
          }, 350);
        })
        .catch(() => {
          /* Low Power Mode etc. — poster carries; first gesture retries. */
        });
    };
    const onCanPlay = (): void => activate();
    const onGesture = (): void => activate();

    const seek = (t: number): void => {
      try {
        // fastSeek snaps to the nearest keyframe — frame-exact on the
        // all-intra full master, but visibly stepped on the GOP-8 mobile tier,
        // which must use an exact currentTime seek instead.
        const fs = (video as HTMLVideoElement & { fastSeek?: (t: number) => void }).fastSeek;
        if (seekMode === 'fast' && typeof fs === 'function') fs.call(video, t);
        else video.currentTime = t;
      } catch {
        /* not seekable yet */
      }
    };

    // ---- scroll → smoothed progress → clock -------------------------------
    let rawP = 0;
    let dispP = 0;
    let lastSet = -1;
    const measure = (): void => {
      const r = section.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      rawP = total > 0 ? clamp01(-r.top / total) : 0;
    };

    // Re-arm the altar loading loop after the tab/app returns to the fore.
    // Backgrounding pauses the muted video and (on iOS) drops its decode
    // context; the rAF loop's own play() isn't reliably honored on return, so
    // we kick it explicitly on visibilitychange / pageshow. If play() is
    // rejected (context lost), reload — the loop re-seeks to the altar and
    // plays once the source is back.
    const kickLoadingPlayback = (): void => {
      if (!loadingRef.current || !video.duration) return;
      const altarStart = video.duration * (5 / 6);
      try {
        if (video.currentTime < altarStart || video.currentTime >= video.duration - 0.15) {
          video.currentTime = altarStart;
        }
      } catch { /* not seekable yet */ }
      video.play().catch(() => {
        try { video.load(); } catch { /* nothing more to do; poster carries */ }
      });
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') kickLoadingPlayback();
    };

    let raf = 0;
    let wasLoading = false;
    const loop = (): void => {
      raf = requestAnimationFrame(loop);
      dispP += (rawP - dispP) * 0.12;
      onProgressRef.current?.(dispP);
      if (!segs) return;

      // LOADING MODE — play the altar chamber (last 1/6 of the timeline) on
      // loop, so the gild renders over a LIVING scene (the leaf trembling, the
      // embers breathing) rather than a frozen frame. Overrides scroll-scrub.
      if (loadingRef.current && video.duration) {
        const dur = video.duration;
        const altarStart = dur * (5 / 6);
        if (video.paused) {
          if (video.currentTime < altarStart) {
            try { video.currentTime = altarStart; } catch { /* not seekable */ }
          }
          video.play().catch(() => {});
        }
        if (video.currentTime >= dur - 0.15) {
          try { video.currentTime = altarStart; } catch { /* not seekable */ }
        }
        wasLoading = true;
        return;
      }
      // Leaving loading → pause and hand the clock back to scroll.
      if (wasLoading) {
        wasLoading = false;
        video.pause();
        lastSet = -1;
      }

      if (!activated) return;
      const t = timeFor(dispP);
      // Only issue a seek once the previous one has completed. Firing a fresh
      // seek every frame (as fast as scroll moves) piles requests the decoder
      // can't service; the browser coalesces and drops them, and a paused
      // <video> is left painting a STALE frame — e.g. scrub hard to the end,
      // then back, and it stays frozen on the last (altar) frame. Gating on
      // .seeking lets each seek finish and repaint; dispP keeps smoothing, so
      // the next seek always targets the latest rest position.
      if (!video.seeking && Math.abs(t - lastSet) > 0.033) {
        lastSet = t;
        seek(t);
      }
    };

    pickSource();
    measure();
    video.addEventListener('loadedmetadata', buildSegs);
    video.addEventListener('canplay', onCanPlay);
    window.addEventListener('touchstart', onGesture, { passive: true });
    window.addEventListener('pointerdown', onGesture, { passive: true });
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(resizeT);
      video.removeEventListener('loadedmetadata', buildSegs);
      video.removeEventListener('canplay', onCanPlay);
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={cn('relative', className)}
      style={{ height: `${heightVh}svh`, backgroundColor: '#09080c' }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          height: '100svh',
          width: '100%',
          overflow: 'hidden',
          backgroundColor: '#09080c',
        }}
      >
        {/* Poster-first: the chamber master carries the page until (and
            whenever) the video can't. */}
        <img
          ref={posterRef}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <video
          ref={videoRef}
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 [&.house-video-ready]:opacity-100"
        />
        {/* Legibility scrim — same vocabulary as the corridor's. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[5]"
          style={{
            background:
              'radial-gradient(125% 95% at 50% 42%, transparent 32%, rgba(0,0,0,0.32) 72%, rgba(0,0,0,0.66) 100%),' +
              'linear-gradient(180deg, rgba(0,0,0,0.46) 0%, rgba(0,0,0,0.1) 24%, rgba(0,0,0,0.1) 52%, rgba(0,0,0,0.8) 100%)',
          }}
        />
        {children}
      </div>
    </section>
  );
}
